import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { backendMessages } from '@heltarchat/shared';

import enPlurals from './enPlurals.json';
import es from './es/translation.json';

/**
 * Natural-language keys: the key IS the English copy (t('Send Message')), so
 * English lives exactly once — in the code. i18next returns the key when a
 * language has no value for it, which makes English the built-in fallback and
 * keeps every locale file translations-only. src/locales/en/translation.json
 * is a build artifact of `pnpm extract-messages` (the translator's source
 * list + coverage input for `i18next-cli status`) and is never imported here.
 *
 * The one exception is enPlurals.json: a key can only spell one number, so
 * copy whose English inflects with `count` ("1 row" vs "2 rows") needs a real
 * English entry. It is hand-maintained and holds `_one`/`_other` variants
 * only — never an identity map.
 *
 * Never rename a key in <lng>/translation.json on its own: the key is the
 * English literal in the source, so renaming one side breaks the match and the
 * string silently falls back to English. Fixing English copy is a re-key —
 * edit the source, hand-move the value to the new key, re-extract, and check
 * nothing came out empty (removeUnusedKeys drops the old key *and* its value).
 *
 * To add a language — five places, and missing any of the last three fails
 * quietly rather than loudly:
 *   1. `locales` in ../../i18next.config.ts, FIRST — extraction does not emit
 *      <lng>/translation.json at all until the language is listed there.
 *   2. `pnpm extract-messages`, then translate the generated file.
 *   3. LANGUAGES below — drives supportedLngs and the settings dropdown.
 *   4. `resources` below, plus a backendMessages<Lng> in @heltarchat/shared
 *      registered in its `backendMessages` map — @see backendMessagesEs.
 *      Without it the backend's error text stays English (and tBackend can
 *      fall through to a UI label instead).
 *   5. DATE_LOCALES in @heltarchat/ui (lib/utils) — Calendar and the date
 *      pickers fall back to en-US for any language missing there.
 * The __tests__ guards derive from LANGUAGES, so 3 also enrols the new
 * language in them.
 */
export const LANGUAGES = {
  en: 'English',
  es: 'Español',
} as const;

export type LanguageCode = keyof typeof LANGUAGES;

export const i18n = i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    resources: {
      en: { translation: enPlurals },
      es: { translation: es, backend: backendMessages.es },
    },
    fallbackLng: 'en',
    supportedLngs: Object.keys(LANGUAGES),
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    keySeparator: false,
    nsSeparator: false,
    returnEmptyString: false,
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
  });

/**
 * Mirror the active language onto <html lang>, which index.html hardcodes to
 * "en". Screen readers rely on it, and @heltarchat/ui reads it to pick the
 * date-fns locale for Calendar/DatePicker — that keeps the component package
 * free of this i18n stack. Language changes never reload, so track the event;
 * the promise covers the initial resolve, which can land before this runs.
 */
const syncHtmlLang = () => {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = (
    i18next.resolvedLanguage ??
    i18next.language ??
    'en'
  ).split('-')[0];
};
i18next.on('languageChanged', syncHtmlLang);
void i18n.then(syncHtmlLang);

/**
 * Translate text handed to a toast (successModal/errorModal/warningModal).
 * It arrives from two places — a backend response message, or a frontend
 * literal marked with tMark() — so both catalogs are tried, backend first.
 * Unknown/dynamic text (ids, counts, Meta error codes) passes through.
 */
export const tBackend = (text: string): string =>
  i18next.t(text, { ns: ['backend', 'translation'], defaultValue: text });
